import React from 'react';
import {ButtonText} from './button'
import {cva, VariantProps} from 'class-variance-authority'
import { cn } from '@/lib/utils'
import {DropdownMenu, DropdownMenuTrigger} from '@/components/ui/DropdownMenu'

interface ButtonProps {
    icon?: React.ReactNode
    label?: React.ReactNode
    className?: string
    menuContent?: React.ReactNode
}


export interface GroupButtonsProps extends React.ButtonHTMLAttributes<HTMLDivElement>, VariantProps<typeof buttonGroupVariants> {
    buttons: ButtonProps[]
}


const buttonGroupVariants = cva(
    'inline-flex text-sm font-medium',
    {
        variants: {
            variant: {
                default: 'bg-primary',
                outline:
                    'border border-primary bg-white disabled:bg-white text-txt-secondary',
            },
            size: {
                sm: 'rounded-sm text-xs',
                md: 'rounded-md',
                lg: 'rounded-lg text-base',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'md',
        },
    }
)

const ButtonOfGroup = React.forwardRef<HTMLDivElement, ButtonProps>  (({ icon, label,className, ...rest }) => {
    return (
        <button className={`flex items-center px-4 py-1 text-white hover:bg-blue-700 ${className}`} {...rest}>
            {icon && (
                <span className="flex items-center justify-center w-6 h-6 text-white rounded-full mr-2">
            {icon}
          </span>
            )}
            <ButtonText>
                {label}
            </ButtonText>
        </button>
    )
})

const ButtonOfGroupOutlined = React.forwardRef<HTMLDivElement, ButtonProps>  (({ icon, label,className,menuContent, ...rest }) => {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    className={`flex items-center px-4 py-1 text-white hover:bg-accent hover:bg-gray-tr-08 text-txt-secondary ${className}`} {...rest}>
                    {icon && (
                        <span className="flex items-center justify-center w-6 h-6 text-txt-secondary rounded-full mr-2">
            {icon}
          </span>
                    )}
                    <ButtonText className='text-txt-secondary'>
                        {label}
                    </ButtonText>
                </button>
            </DropdownMenuTrigger>
            {menuContent}
        </DropdownMenu>
    )
})

export const GroupButtons = React.forwardRef<HTMLDivElement,GroupButtonsProps> (({ buttons, variant,className }) => {
    return (
        <>
            {variant === 'default' && (
                <div className={cn(buttonGroupVariants({variant, className}))}>
                    {buttons.map((button, index) => (
                        <ButtonOfGroup
                            key={index}
                            icon={button.icon}
                            label={button.label}
                            className={`${
                                index === 0
                                    ? 'rounded-l-md'
                                    : index === buttons.length - 1
                                        ? 'rounded-r-md border-l'
                                        : 'border-l'
                            }`}
                        />
                    ))}
                </div>
            )}
            {variant === 'outline' && (
                <div className={cn(buttonGroupVariants({variant, className}))}>
                    {buttons.map((button, index) => {
                        const {icon, label, menuContent, ...rest} = button
                        return (
                        <ButtonOfGroupOutlined
                            key={index}
                            icon={icon}
                            label={label}
                            menuContent={menuContent}
                            className={`${
                                index === 0
                                    ? 'rounded-l-md'
                                    : index === buttons.length - 1
                                        ? 'rounded-r-md border-l'
                                        : 'border-l'
                            }`}
                            {...rest}
                        />
                        )
                    })}
                </div>
            )}
        </>
    )
})
